import {
	Component,
	ElementRef,
	inject,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { ActivatedRoute } from '@angular/router';
import {
	animate,
	state,
	style,
	transition,
	trigger,
} from '@angular/animations';
import { combineLatest, firstValueFrom, Subject, Subscription, takeUntil } from 'rxjs';
import dayjs from 'dayjs';
import { UserService } from '@lmc-app/core/services/user.service';
import { User } from '@lmc-app/shared/interface/user.interface';
import { MatDialog } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { Loading } from '@lmc-app/applications/loading';
import { Permissions } from '@lmc-app/store';
import { ApplicationService } from '@lmc-app/core/services/application.service';
import { RapidUserAccountService } from '@lmc-app/core/services/rapid-user-account.service';
import { RapidConsentModalComponent } from './rapid-consent-modal/rapid-consent-modal.component';
import { RapidApplicationModalComponent } from './rapid-application-modal/rapid-application-modal.component';
import { RapidCreditorsModalComponent } from './rapid-creditors-modal/rapid-creditors-modal.component';
import { RapidResultModalComponent } from './rapid-result-modal/rapid-result-modal.component';
import { ApplicationSearchService } from '@lmc-app/core/services/application-search.service';
import { resultMemoize } from '@ngrx/store';
import { ListenerService } from '@lmc-app/core/services/listener.service';
import {
	ApplicationUpdatedDialogComponent,
	ApplicationUpdatedDialogData,
	ApplicationUpdatedDialogResult,
} from './application-updated-dialog/application-updated-dialog.component';

@Component({
	selector: 'app-summary',
	templateUrl: './summary.component.html',
	styleUrls: ['./summary.component.scss'],
	animations: [
		trigger('sideNavMenuAnimationState', [
			state(
				'topX',
				style({
					transform: 'rotate(45deg)',
					transformOrigin: 'left',
					margin: '7px',
				})
			),
			state(
				'hide',
				style({
					opacity: 0,
				})
			),
			state(
				'bottomX',
				style({
					transform: 'rotate(-45deg)',
					transformOrigin: 'left',
					margin: '7px',
				})
			),
			state('line', style({})),
			state(
				'right',
				style({
					marginTop: '2px',
					marginLeft: '2px',
				})
			),
			state(
				'left',
				style({
					transform: 'rotate(-180deg)',
					transformOrigin: 'center',
					marginBottom: '2px',
					marginRight: '2px',
				})
			),
			transition(':enter', []),
			transition('* => *', [animate('0.2s')]),
		]),
	],
})
export class SummaryComponent implements OnInit, OnDestroy {
	private parentSub = new Subscription();
	@ViewChild('contentArea') public contentAreaRef!: ElementRef;
	protected applicationId: string = "";
	protected application: any = null;
	protected model: any = null;
	protected permissions: Permissions | null = null;

	protected isExpanded: boolean = true;
	private _selectedTab: string = "";
	// Tabs are created the first time they are opened and kept alive afterwards, so hidden tabs don't load their data on page load
	private readonly loadedTabs = new Set<string>();
	protected menuItems: any[] = [];

	protected currentUser!: User;
	protected _onDestroy = new Subject<void>();
	private readonly _snackBar = inject(MatSnackBar);
	private readonly _bottomSheet = inject(MatBottomSheet);
	private scrollPositions = new Map<string, number>();
	protected isShowPrimaryConsentModal: boolean = false;
	protected isShowCoapplicantConsentModal: boolean = false;
	protected isCoapplicantConsentPulled: boolean = false;
	protected isConsentClick: boolean = false;
	protected detailSaveDate!: Date;
	protected detailCancelDate!: Date;
	protected detailEditDate!: Date;
	protected savedDate!: Date;
	private isCheckingForUpdates: boolean = false;
	// True while the application is being (re)loaded, e.g. right after a tab saves; the update check waits for it
	private isLoadingApplication: boolean = false;

	protected oldCalculatorAccess = [27,7,148,54,201,4,244,627,1133,1134,1140] ; 		// To be removed after loan calculator update

	constructor(
		private readonly dialog: MatDialog,
		private readonly apiService: LegacyApiService,
		private readonly applicationService: ApplicationService,
		private readonly rapidUserAccountService: RapidUserAccountService,
		private readonly activatedRoute: ActivatedRoute,
		private readonly userService: UserService,
		private _appSearchService: ApplicationSearchService,
		private _listenerService: ListenerService,
	) { }

	public ngOnInit(): void {
		this.setupPermissionSubscriptions();

		let URI = window.location.href; // URI for ZOHO side links
		let queryString = URI?.split('/'); // Split the url
		const tabName = queryString?.slice(-1)?.[0]; // Get the last element name
		if (!containsSpecialChars(tabName)) {
			// If last element doesn't contain special chars
			this.selectedTab = tabName;
		}
	}

	private getApplication(applicationId: string): void {
		this.isLoadingApplication = true;
		this.parentSub.add(this.applicationService.getApplication(applicationId).subscribe({
			next: (res) => {
				this.isLoadingApplication = false;
				this.application = res;
				this.isShowPrimaryConsentModal = false;
				this.isShowCoapplicantConsentModal = false;
				this.parentSub.add(this.applicationService.getApplicationConfig().subscribe({
					next: (res) => {
						if (res.isShowPullConsentModal) {
							if (this.application.Deal_Name && this.application.Loyalty_Payment_Plan && this.application.Residency_Status
								&& this.application.Date_of_Birth && this.application.Email1
								&& (this.application.Are_you_currently_a_Home_Owner === "No"
									|| (this.application.Are_you_currently_a_Home_Owner === "Yes" && this.application.MTG_Balance && this.application.Estimated_Value))
								&& this.application.Address1 && this.application.City && this.application.Postal_Code && this.application.Province
								&& this.application.Mobile
							) {
								this.isShowPrimaryConsentModal = true;
							}
							if (this.application.Co_Applicant_Name && this.application.Guarantor_Name && this.application.Co_App_Residency_Status
								&& this.application.Co_Applicant_Date_of_Birth && this.application.Email
								&& this.application.Co_Applicant_Property_Address && this.application.Co_Applicant_City && this.application.Co_Applicant_Postal_Code && this.application.Co_Applicant_Province
								&& this.application.Secondary_Cell_Number
							) {
								// this.isShowCoapplicantConsentModal = true; // Disabled the co-applicant Consent Modal until further discussino
							}
							this.application._isPrimaryConsentPulled = false;
							this.isCoapplicantConsentPulled = false;
							this.parentSub.add(this.apiService.checkIfTUReportExists(this.applicationId, "primary")
								.subscribe(async (res: any) => {
									if (res?.data && res?.data.length > 0) {
										this.application._isPrimaryConsentPulled = true;
										this.parentSub.add(this.apiService.checkIfTUReportExists(this.applicationId, "co_applicant")
											.subscribe(async (res: any) => {
												if (res?.data && res?.data.length > 0) {
													this.isCoapplicantConsentPulled = true;
												}
												else {
													this.checkOpenConsentModal("co_applicant");
												}
											}));
									}
									else {
										this.checkOpenConsentModal("primary");
									}
								}
							));							
						}
								
					},
					error: (error) => {
						console.error(error);
						this._snackBar.open(
							error,
							SnackbarConstants.ACTION_DISMISS,
							SnackbarConstants.DURATION_DISMISS_SHORT
						);
					},
				}));
				// Insert into page history searches for any application get
				if (this.application.Deal_Name) {
					this.parentSub.add(this._appSearchService
						.insertPageHistory(
							this.application.Deal_Name,
							this.application.id,
							this.currentUser.email
						)
						.subscribe((res) => {
							if(!res?.status) {
								console.log(res, 'Error insert page history!');
							}
						}
					));
				}
			},
			error: (error) => {
				this.isLoadingApplication = false;
				console.error(error);
				this._snackBar.open(
					error,
					SnackbarConstants.ACTION_DISMISS,
					SnackbarConstants.DURATION_DISMISS_SHORT
				);
			},
		}));
	}

	private setupPermissionSubscriptions(): void {
		this.parentSub.add(combineLatest([this.userService.user$, this.userService.permissions$])
			.pipe(takeUntil(this._onDestroy))
			.subscribe(([user, permissions]) => {
				if (user && permissions) {
					this.currentUser = user;
					this.permissions = permissions;
					this.updateMenuItems();

					this.parentSub.add(this.activatedRoute?.params?.subscribe((params) => {
						this.applicationId = params?.['id'];
						this.getApplication(this.applicationId);
						// Hotfix side nav old calculator
						this.refreshWaiveOffStatus(this.applicationId)
					}));

					this.parentSub.add(this.activatedRoute?.queryParams?.subscribe((params) => {
						this.selectedTab = params?.['selectedTab'] || 'activities';
					}));
				}
			}));
	}

	public ngOnDestroy(): void {
		this.parentSub.unsubscribe();
		this._onDestroy.next();
		this._onDestroy.complete();
	}

	private updateMenuItems(): void {
		this.menuItems = [
			{
				id: 'activities',
				name: 'Activities',
				icon: 'pending_actions',
				isVisible: this.permissions?.pandaDocsAccessRoles.includes(
					this.currentUser.roleID
				),
			},
			{
				id: 'details',
				name: 'Details',
				icon: 'wysiwyg',
				isVisible: true,
				expanded: true,
				children: [
					{
						id: 'details-general',
						name: 'General',
						icon: 'info',
						isVisible: true,
					},
					{
						id: 'details-client',
						name: 'Client',
						icon: 'person',
						isVisible: true,
					},
					{
						id: 'details-client-emergency-contacts',
						name: 'Client EC',
						// 'groups_2' is the valid Material icon name ('groups2' rendered as the 'groups' glyph + a literal "2")
						icon: 'groups_2',
						isVisible: true,
					},
					{
						id: 'details-coapplicant',
						name: 'Co-Applicant',
						icon: 'group',
						isVisible: this.model?.Co_Appl_Required === "Yes",
					},
					{
						id: 'details-coapplicant-emergency-contacts',
						name: 'Co-Applicant EC',
						icon: 'groups_2',
						isVisible: this.model?.Co_Appl_Required === "Yes",

					},
				],
			},
			{
				id: 'fulfillment',
				name: 'Fulfillment',
				icon: 'backup',
				isVisible: this.permissions?.pandaDocsAccessRoles.includes(
					this.currentUser.roleID
				),
			},
			{
				id: 'agreements',
				name: 'Agreements',
				icon: 'handshake',
				isVisible: true,
				expanded: true,
				children: [
					{
						id: 'agreements-create',
						name: 'Create',
						icon: 'note_add',
						isVisible: true,
					},
					{
						id: 'agreements-list',
						name: 'List',
						icon: 'view_headline',
						isVisible: true,
					},
				],
			},
			{
				id: 'checklist',
				name: 'Checklist',
				icon: 'check_box',
				isVisible: this.permissions?.checklistAccessRoles.includes(
					this.currentUser.roleID
				),
			},
			{
				id: 'underwriting',
				name: 'Underwriting',
				icon: 'assignment',
				isVisible: this.permissions?.underwritingAccessRoles.includes(
					this.currentUser.roleID
				),
			},
			{
				id: 'calculator',
				name: 'Loan Calculator',
				icon: 'calculate',
				isVisible: this.permissions?.loanCalculatorAccessRoles.includes(
					this.currentUser.roleID
				),
			},
			{
				id: 'transunion',
				name: 'Transunion',
				icon: 'credit_score',
				isVisible: this.permissions?.transunionAccessRoles.includes(
					this.currentUser.roleID
				),
			},
			{
				id: 'oldCalculator',
				name: 'Old Calculator',
				icon: 'calculate',
				isVisible: !this.isSahaCalculator || (this.applicationId === '2390707000034288237' || this.oldCalculatorAccess.includes(this.currentUser?.employeeID)) // 2390707000034288237 lpp test
				// isVisible: this.permissions?.oldloanCalculatorAccessRoles.includes(
				// 	this.currentUser.employeeID
				// ),
			},
		];
	}

	protected toggleSidenav(): void {
		this.isExpanded = !this.isExpanded;
	}

	protected selectTab(tabId: string): void {
		if (tabId.startsWith("details")) {
			this.selectedTab = tabId;
		}
		else {
			this.saveCurrentScrollPosition();
			this.selectedTab = tabId;
			setTimeout(() => {
				this.restoreScrollPosition(tabId);
			}, 0);
		}
		// When clicking List from Agreement close the preview and reload the list
		if (tabId === 'agreements-list') {
			this._listenerService.emit('agreementsListTabSelected', true);
		}
		// When clicking Create from Agreement reset back to step 1
		if (tabId === 'agreements-create') {
			this._listenerService.emit('agreementsCreateTabSelected', true);
		}
		this.checkForUpdates();
	}

	/**
	 * Re-fetch the application and, if someone else saved it after it was loaded, ask the user to reload it.
	 * Compares Override_Type, the "name (timestamp)" stamp our saves write: saves made on this page reload it,
	 * and Zoho automations don't touch it, unlike Modified_Time
	 */
	private checkForUpdates(): void {
		// Skip while reloading: the shown application is about to be replaced, so its stamp is stale
		// The warning on tab change is switched on per user by _staleDataNotification.showStaleOnTabs
		if (!this.currentUser?._staleDataNotification?.showStaleOnTabs
			|| !this.application?.id || this.isCheckingForUpdates || this.isLoadingApplication) {
			return;
		}

		this.isCheckingForUpdates = true;
		this.parentSub.add(this.applicationService.getApplication(this.application.id).subscribe({
			next: (latestApplication) => {
				// Compare with the application shown now, which may have been refreshed while this request was in flight
				if (!latestApplication?.Override_Type
					|| this.isLoadingApplication
					|| latestApplication.id !== this.application?.id
					|| latestApplication.Override_Type === this.application.Override_Type) {
					this.isCheckingForUpdates = false;
					return;
				}

				const dialogRef = this.dialog.open<ApplicationUpdatedDialogComponent, ApplicationUpdatedDialogData, ApplicationUpdatedDialogResult>(
					ApplicationUpdatedDialogComponent,
					{
						width: '480px',
						maxWidth: '90vw',
						disableClose: true,
						data: {
							modifiedTime: dayjs(latestApplication.Modified_Time).format('YYYY-MM-DD h:mm A'),
							lastDetailsSave: latestApplication.Override_Type || '',
						},
					}
				);

				this.parentSub.add(dialogRef.afterClosed().subscribe((result) => {
					this.isCheckingForUpdates = false;
					if (result === 'reload') {
						// Same reload the tabs trigger after saving
						this.getApplication(this.application.id);
					}
				}));
			},
			error: (error) => {
				// A failed check should not interrupt the user
				console.error(error);
				this.isCheckingForUpdates = false;
			},
		}));
	}

	private saveCurrentScrollPosition(): void {
		if (this.contentAreaRef?.nativeElement) {
			const scrollTop = this.contentAreaRef.nativeElement.scrollTop;
			this.scrollPositions.set(this.selectedTab, scrollTop);
		}
	}

	private restoreScrollPosition(tabId: string): void {
		if (this.contentAreaRef?.nativeElement) {
			const savedPosition = this.scrollPositions.get(tabId) || 0;
			this.contentAreaRef.nativeElement.scrollTop = savedPosition;
		}
	}

	protected onApplicationChanged(application: any): void {
		this.applicationId = application.id;
		this.getApplication(application.id);
	}

	protected onModelChanged(model: any): void {
		this.model = model;
		this.updateMenuItems();
	}

	private checkOpenConsentModal(application_type: string): void {
		switch (application_type) {
			case "primary":
				if (this.isShowPrimaryConsentModal && !this.application._isPrimaryConsentPulled) {
					this.openConsentModal(application_type);
				}
				break;
			case "co_applicant":
				if (this.isShowCoapplicantConsentModal && !this.isCoapplicantConsentPulled) {
					this.openConsentModal(application_type);
				}
				break;
		}
	}

	private isHit(TU_Report: any) {
		try {
			if (TU_Report.REL4Report.TU_FFR_Report[0].Hit[0] == 'Y') return true;
			else return false;
		} catch (err) {
			return false;
		}
	}

	private async updateTransunionDB(info: any, tuReport: any): Promise<{ insertedTransunionID: any }> {
		return new Promise((resolve, reject) => {
			this.parentSub.add(this.apiService
				.updateTransunionDB(info, tuReport)
				.subscribe((res: any) => {
					resolve(res);
				}));
		});
	}

	private async addRapidClient(info: any, fileStatus: boolean): Promise<{ insertedRapidClientID: any }> {
		return new Promise((resolve, reject) => {
			this.parentSub.add(this.rapidUserAccountService
				.getRapidUserAccount("generic")
				.subscribe((genericRapidUser) => {
					this.parentSub.add(this.apiService
						.addRapidClient(info, fileStatus, genericRapidUser)
						.subscribe((res: any) => {
							resolve(res);
						}));
				}));
		});
	}

	private updateTransunionApplication(info: any) {
		return new Promise((resolve, reject) => {
			this.parentSub.add(this.apiService.updateTransunionApplication(info).subscribe((res) => {
				resolve(res);
			}));
		});
	}

	private updateRapidClientApplication(
		info: any,
		fileStatus: boolean,
		rapidStatus: any,
		other: any
	) {
		return new Promise((resolve, reject) => {
			this.parentSub.add(this.apiService
				.updateRapidClientApplication(info, fileStatus, rapidStatus, other)
				.subscribe((res) => {
					resolve(res);
				}));
		});
	}

	private saveRapidInternalResultNote(note: string): void {
		const noteInfo = {
			subject: "Rapid Internal Result",
			description: "",
			communication: "INT",
			RLI_Called_Count: "",
			email_address: "",
			email_notes: "",
			note,
		};
		this.parentSub.add(this.apiService
			.createNoteLocalDB(
				noteInfo,
				this.application.Deal_Name,
				this.application.id,
				this.currentUser?.full_name,
				this.currentUser?.zoho_owner_id,
				false,
				'',
			)
			.subscribe((res) => {
				if (res.message == 'Success') {
					let app_note_id = res['data'][0]['app_note_id'] ?? '';
					// Push to ZOHO
					this.parentSub.add(this.apiService
						.addLoanInterviewNotesZOHO(
							noteInfo,
							this.application.id,
							this.currentUser?.zoho_owner_id
						)
						.subscribe((res) => {
							if (res['message']['statusCode'] === 201) {
								let zoho_note_id = res['app_id'];
								this.apiService
									.updateNoteIDLocal(app_note_id, zoho_note_id)
									.subscribe(() => {
										// console.log(res)
									});

								this._snackBar.open('Note saved successfully!', 'Close', {
									duration: 3000,
									panelClass: ['success-snackbar'],
								});
							} else {
								this.parentSub.add(this.apiService
									.emailNotesToIT(
										noteInfo,
										this.application.id,
										this.application.Deal_Name,
										this.currentUser?.full_name
									)
									.subscribe((res) => {
										console.log(res);
									}));
								console.error(res, 'ZOHO Note push failed!');
								this._snackBar.open('Failed to save note to ZOHO', 'Close', {
									duration: 5000,
									panelClass: ['error-snackbar'],
								});
							}
						}));
				} else {
					this._snackBar.open('Failed to save note!', 'Close', {
						duration: 5000,
						panelClass: ['error-snackbar'],
					});
					console.error(res, 'Platform: ZOHO Note save failed!');
				}
			}));
	}

	private addCheckBoxestoTrades(tuReport: { REL4Report: { TU_FFR_Report: { Trades: { Trade: any }[], Collections: { Collection: any }[], Legals: { Legal: any }[], Bankings: { Banking: any }[], Bankruptcies: { Bankruptcy: any }[], ScoreProducts: { ScoreProduct: any }[] }[] } }): { REL4Report: { TU_FFR_Report: { Trades: { Trade: any }[], Collections: { Collection: any }[], Legals: { Legal: any }[], Bankings: { Banking: any }[], Bankruptcies: { Bankruptcy: any }[], ScoreProducts: { ScoreProduct: any }[] }[] } } {
		const TU_Report_Trade =
			tuReport.REL4Report?.TU_FFR_Report?.[0]?.Trades?.[0]?.Trade;
		if (TU_Report_Trade) {
			if (!TU_Report_Trade?.[0].hasOwnProperty('Referral')) {
				for (var i = 0; i < TU_Report_Trade?.length; i++) {
					TU_Report_Trade[i]['Referral'] = false;
				}
			}
			if (!TU_Report_Trade?.[0].hasOwnProperty('Cons')) {
				for (var i = 0; i < TU_Report_Trade?.length; i++) {
					TU_Report_Trade[i]['Cons'] = false;
				}
			}
		}
		return tuReport;
	}
	private addCheckBoxestoCollections(tuReport: { REL4Report: { TU_FFR_Report: { Trades: { Trade: any }[], Collections: { Collection: any }[], Legals: { Legal: any }[], Bankings: { Banking: any }[], Bankruptcies: { Bankruptcy: any }[], ScoreProducts: { ScoreProduct: any }[] }[] } }): { REL4Report: { TU_FFR_Report: { Trades: { Trade: any }[], Collections: { Collection: any }[], Legals: { Legal: any }[], Bankings: { Banking: any }[], Bankruptcies: { Bankruptcy: any }[], ScoreProducts: { ScoreProduct: any }[] }[] } } {
		const TU_Report_Collection =
			tuReport.REL4Report?.TU_FFR_Report?.[0]?.Collections?.[0]
				?.Collection;
		if (TU_Report_Collection) {
			if (!TU_Report_Collection?.[0].hasOwnProperty('Referral')) {
				for (var i = 0; i < TU_Report_Collection?.length; i++) {
					TU_Report_Collection[i]['Referral'] = false;
				}
			}
			if (!TU_Report_Collection?.[0].hasOwnProperty('Cons')) {
				for (var i = 0; i < TU_Report_Collection?.length; i++) {
					TU_Report_Collection[i]['Cons'] = false;
				}
			}
		}
		return tuReport;
	}

	private addCheckBoxestoLegals(tuReport: { REL4Report: { TU_FFR_Report: { Trades: { Trade: any }[], Collections: { Collection: any }[], Legals: { Legal: any }[], Bankings: { Banking: any }[], Bankruptcies: { Bankruptcy: any }[], ScoreProducts: { ScoreProduct: any }[] }[] } }): { REL4Report: { TU_FFR_Report: { Trades: { Trade: any }[], Collections: { Collection: any }[], Legals: { Legal: any }[], Bankings: { Banking: any }[], Bankruptcies: { Bankruptcy: any }[], ScoreProducts: { ScoreProduct: any }[] }[] } } {
		const TU_Report_Legal =
			tuReport.REL4Report?.TU_FFR_Report?.[0]?.Legals?.[0]?.Legal;
		if (TU_Report_Legal) {
			if (!TU_Report_Legal?.[0].hasOwnProperty('Referral')) {
				for (var i = 0; i < TU_Report_Legal?.length; i++) {
					TU_Report_Legal[i]['Referral'] = false;
				}
			}
			if (!TU_Report_Legal?.[0].hasOwnProperty('Cons')) {
				for (var i = 0; i < TU_Report_Legal?.length; i++) {
					TU_Report_Legal[i]['Cons'] = false;
				}
			}
		}
		return tuReport;
	}

	private createMortgageList(tuReport: { REL4Report: { TU_FFR_Report: { Trades: { Trade: any }[], Collections: { Collection: any }[], Legals: { Legal: any }[], Bankings: { Banking: any }[], Bankruptcies: { Bankruptcy: any }[], ScoreProducts: { ScoreProduct: any }[] }[] } }): [{ REL4Report: { TU_FFR_Report: { Trades: { Trade: any }[], Collections: { Collection: any }[], Legals: { Legal: any }[], Bankings: { Banking: any }[], Bankruptcies: { Bankruptcy: any }[], ScoreProducts: { ScoreProduct: any }[] }[] } }, any[], any[]] {
		let mortgages = [];
		let trades = [];
		const TU_Report_Trade =
			tuReport.REL4Report?.TU_FFR_Report?.[0]?.Trades?.[0]?.Trade;
		if (TU_Report_Trade) {
			for (var i = 0; i < TU_Report_Trade?.length; i++) {
				if (TU_Report_Trade?.[i]?.Type == 'M') {
					mortgages.push(TU_Report_Trade[i]);
				} else {
					trades.push(TU_Report_Trade[i]);
				}
			}
		}
		return [tuReport, mortgages, trades];
	}

	private async getTransunionCodes(): Promise<any> {
		return new Promise((resolve, reject) => {
			let result = this.apiService.getTransunionCodes().subscribe((res: { status: any, data: any }) => {
				if (res['status']) {
					resolve(res['data']);
				} else {
					alert('Error getting Transunion Codes');
					resolve(false);
				}
			});
		});
	}

	private async transformTransunionReport(tuReport: { REL4Report: { TU_FFR_Report: { Trades: { Trade: any }[], Collections: { Collection: any }[], Legals: { Legal: any }[], Bankings: { Banking: any }[], Bankruptcies: { Bankruptcy: any }[], ScoreProducts: { ScoreProduct: any }[] }[] } }) {
		tuReport = this.addCheckBoxestoTrades(tuReport);
		tuReport = this.addCheckBoxestoCollections(tuReport);
		tuReport = this.addCheckBoxestoLegals(tuReport);
		let mortgages, trades;
		[tuReport, mortgages, trades] = this.createMortgageList(tuReport);
		let transunion_codes = await this.getTransunionCodes();
		if (transunion_codes) {
			if (trades) {
				for (var i = 0; i < trades.length; i++) {
					let n1;
					try {
						n1 = transunion_codes.find(
							(x: { NarrativeCode: any }) => x['NarrativeCode'] === trades[i]['Narrative1'][0]
						);
					} catch (err) { }
					if (n1) trades[i]['Description1'] = n1['Description'];
					else trades[i]['Description1'] = '';
					let n2;
					try {
						n2 = transunion_codes.find(
							(x: { NarrativeCode: any }) => x['NarrativeCode'] === trades[i]['Narrative2'][0]
						);
					} catch (err) { }
					if (n2) trades[i]['Description2'] = n2['Description'];
					else trades[i]['Description2'] = '';
				}
			}
			//
			if (mortgages) {
				for (var i = 0; i < mortgages.length; i++) {
					let n1;
					try {
						n1 = transunion_codes.find(
							(x: { NarrativeCode: any }) => x['NarrativeCode'] === mortgages[i]['Narrative1'][0]
						);
					} catch (err) { }
					if (n1) mortgages[i]['Description1'] = n1['Description'];
					else mortgages[i]['Description1'] = '';
					let n2;
					try {
						n2 = transunion_codes.find(
							(x: { NarrativeCode: any }) => x['NarrativeCode'] === mortgages[i]['Narrative2'][0]
						);
					} catch (err) { }
					if (n2) mortgages[i]['Description2'] = n2['Description'];
					else mortgages[i]['Description2'] = '';
				}
			}
			//
			if (
				tuReport.REL4Report.TU_FFR_Report[0].Collections
			) {
				for (
					var i = 0;
					i <
					tuReport.REL4Report.TU_FFR_Report[0].Collections[0].Collection
						.length;
					i++
				) {
					let n1;
					try {
						n1 = transunion_codes.find(
							(x: { NarrativeCode: any }) =>
								x['NarrativeCode'] ===
								tuReport.REL4Report.TU_FFR_Report[0].Collections[0]
									.Collection[i]['Narrative'][0]
						);
					} catch (err) { }
					if (n1)
						tuReport.REL4Report.TU_FFR_Report[0].Collections[0].Collection[
							i
						]['Description'] = n1['Description'];
					else
						tuReport.REL4Report.TU_FFR_Report[0].Collections[0].Collection[
							i
						]['Description'] = '';
				}
			}
			//
			if (
				tuReport.REL4Report.TU_FFR_Report[0].Bankings
			) {
				for (
					var i = 0;
					i <
					tuReport.REL4Report.TU_FFR_Report[0].Bankings[0].Banking.length;
					i++
				) {
					let n1;
					try {
						n1 = transunion_codes.find(
							(x: { NarrativeCode: any }) =>
								x['NarrativeCode'] ===
								tuReport.REL4Report.TU_FFR_Report[0].Bankings[0].Banking[
								i
								]['Narrative'][0]
						);
					} catch (err) { }
					if (n1)
						tuReport.REL4Report.TU_FFR_Report[0].Bankings[0].Banking[i][
							'Description'
						] = n1['Description'];
					else
						tuReport.REL4Report.TU_FFR_Report[0].Bankings[0].Banking[i][
							'Description'
						] = '';
				}
			}
			//
			if (
				tuReport.REL4Report.TU_FFR_Report[0].Bankruptcies
			) {
				for (
					var i = 0;
					i <
					tuReport.REL4Report.TU_FFR_Report[0].Bankruptcies[0].Bankruptcy
						.length;
					i++
				) {
					let n1;
					try {
						n1 = transunion_codes.find(
							(x: { NarrativeCode: any }) =>
								x['NarrativeCode'] ===
								tuReport.REL4Report.TU_FFR_Report[0].Bankruptcies[0]
									.Bankruptcy[i]['Narrative'][0]
						);
					} catch (err) { }
					if (n1)
						tuReport.REL4Report.TU_FFR_Report[0].Bankruptcies[0].Bankruptcy[
							i
						]['Description'] = n1['Description'];
					else
						tuReport.REL4Report.TU_FFR_Report[0].Bankruptcies[0].Bankruptcy[
							i
						]['Description'] = '';
				}
			}
		}
		return [tuReport, mortgages, trades];
	}

	private openConsentModal(applicationType: string): void {
		let prequalify = {
			income_1200: false,
			residency: false,
			employed: false,
			bankruptcy: false,
			reside_quebec: false,
			native_reserve: false,
			payday_loans: true,
			nsfs: true,
			not_self_employed: true,
		};
		let data = {};
		switch (applicationType) {
			case "primary":
				prequalify = {
					...prequalify,
					income_1200: this.application.Do_you_earn_at_least_1200_per_month === "Yes",
					employed: ["Full-Time", "Part-Time", "Retired working full time", "Retired working part time", "Contracted", "Temporary Work",].includes(this.application.Employment_Status),
					bankruptcy: ["Discharged", "None"].includes(this.application.Bankruptcy_CC_CP),
					reside_quebec: this.application.Province1 !== "Quebec",
					native_reserve: this.application.native_reserve === "Yes",
				};
				data = {
					resident_status: this.application.Residency_Status,
					home_owner: this.application.Are_you_currently_a_Home_Owner,
					loan_amount: this.application.Amount,
					loyalty_program: this.application.Loyalty_Payment_Plan,
					first_name: this.application.Contact_Name?.First_Name,
					middle_name: this.application.Contact_Name?.Middle_Name,
					last_name: this.application.Contact_Name?.Last_Name,
					sin: this.application.S_I_N,
					email_address: this.application.Email1,
					dob: this.application.Date_of_Birth,
					telephone: this.application.Mobile,
					street: this.application.Address1,
					address2: this.application.Address_Line_2,
					city: this.application.City,
					province: this.application.Province,
					postal_code: this.application.Postal_Code,
					pull_consent: true,
					referral_agent_ext: this.application.Referred_By.name,
					comments: "",
					payment_method: this.application.Payment_Method,
					edp_number: this.application.Payment_Method.Acc_Number,
					mortgage_balance: this.application.MTG_Balance,
					estimated_value: this.application.Estimated_Value,
				};
				break;
			case "co_applicant":
				prequalify = {
					...prequalify,
					income_1200: false,
					employed: ["Full-Time", "Part-Time", "Retired working full time", "Retired working part time", "Contracted", "Temporary Work",].includes(this.application.Co_App_Employment_Type),
					bankruptcy: ["Discharged", "None"].includes(this.application.Bankruptcy_CC_CP),
					reside_quebec: !["Quebec", "QC"].includes(this.application.Co_Applicant_Province.trim()),
					native_reserve: false,
				};
				data = {
					resident_status: this.application.Co_App_Residency_Status,
					home_owner: "No",
					loan_amount: this.application.Amount,
					loyalty_program: false,
					first_name: this.application.Co_Applicant_Name,
					middle_name: this.application.Co_Applicant_Middle_Name,
					last_name: this.application.Guarantor_Name,
					sin: this.application.Secondary_S_I_N,
					email_address: this.application.Email,
					dob: this.application.Co_Applicant_Date_of_Birth,
					telephone: this.application.Secondary_Cell_Number,
					street: this.application.Co_Applicant_Property_Address,
					address2: this.application.Co_Applicant_Address_Line_2_Unit_number,
					city: this.application.Co_Applicant_City,
					province: this.application.Co_Applicant_Province,
					postal_code: this.application.Co_Applicant_Postal_Code,
					pull_consent: true,
					referral_agent_ext: this.application.Referred_By.name,
					comments: "",
					payment_method: "",
					edp_number: "",
					mortgage_balance: "",
					estimated_value: "",
				};
				break;
			default:
				break;
		}

		const dialogRapidConsentRef = this.dialog.open(RapidConsentModalComponent, {
			width: '500px',
			data: { application_type: applicationType },
		});

		this.parentSub.add(dialogRapidConsentRef.afterClosed().subscribe((isConfirm) => {
			if (isConfirm) {
				const dialogRapidApplicationRef = this.dialog.open(RapidApplicationModalComponent, {
					width: '1000px',
					data,
				});

				this.parentSub.add(dialogRapidApplicationRef.afterClosed().subscribe((client) => {
					if (client) {
						this._bottomSheet.open(Loading, {
							disableClose: true,
							data: { message: 'Pulling Credit' },
						});
						this.parentSub.add(this.apiService
							.getTransunionCreditReport(client)
							.subscribe(async (res) => {
								const [tuReport, mortgages, trades] = await this.transformTransunionReport(res);
								if (this.isHit(tuReport)) {
									const info = {
										prequalify,
										client,
										loginInfo: this.currentUser,
										internalReferralData: {
											company_id: this.currentUser?.company_id,
											company_name: this.currentUser?.company_name,
											agency_zoho_id: this.currentUser?.agency_zoho_id,
											agent_name: this.application.Account_Name.name,
										},
									};

									await this.updateTransunionDB(
										{
											...client,
											dob: dayjs(client.dob).format('YYYY-MM-DD'),
											application_id: this.applicationId,
											application_type: applicationType,
											loan_number: this.application?.Application_Number,
											pulled_by: this.currentUser.employeeID,
										},
										tuReport,
									);

									this.parentSub.add(this.apiService.checkIfTUReportExists(this.applicationId, "primary")
										.subscribe(async (res: any) => {
											if (res?.data && res?.data.length > 0) {
												const insertedTransunionID = res?.data[0].rowid;

												this.parentSub.add(this.apiService.updateZohoApplication_Transunion(
													tuReport,
													this.applicationId,
													applicationType).subscribe());

												this._bottomSheet.dismiss();

												this._snackBar.open(
													'Pull Credit successful',
													SnackbarConstants.ACTION_DISMISS,
													SnackbarConstants.DURATION_DISMISS_SHORT
												);

												if (applicationType === "primary") {
													const { insertedRapidClientID } = await this.addRapidClient(
														info,
														false,
													);

													const insertedIDs = { insertedTransunionID, insertedRapidClientID };

													const dialogCreditorsRef = this.dialog.open(RapidCreditorsModalComponent, {
														width: '600px',
														data: { zoho_app_id: this.applicationId, tuReport, receivedRapidData: info },
													});

													const creditScore = tuReport.REL4Report.TU_FFR_Report[0].ScoreProducts[0].ScoreProduct[0].Score[0];

													this.parentSub.add(dialogCreditorsRef.afterClosed().subscribe((data) => {
														if (data) {
															let approval_status = false;
															let rapid_status = 'Declined';
															let approvalMessage = "";
															let declineMessage = "";
															let fallBackStatus: boolean;
															let fallBackAmount: boolean;
															const collectorInputs = {
																postal_code: info.client.postal_code,
																loyalty_program: info.client.loyalty_program,
																home_owner: info.client.home_owner,
																creditor_text: data.otherCreditorObject.creditor,
																city: info.client.city,
																province: info.client.province,
																mortgage_balance: info.client.mortgage_balance,
																estimated_value: info.client.estimated_value,
																company_id: this.currentUser?.company_id,
															};
															this._bottomSheet.open(Loading, {
																disableClose: true,
																data: { message: 'Rapid Scoring' },
															});
															this.parentSub.add(this.apiService
																.rapidScoringModel(
																	info.client.loan_amount,
																	data.tuReport,
																	collectorInputs,
																	info
																)
																.subscribe(async (res: any) => {
																	this._bottomSheet.dismiss();
																	rapid_status = res?.result.rapid_status;
																	if (res.result?.hasOwnProperty('approved')) {
																		approval_status = true;
																		approvalMessage = 'approved.'
																	} else if (res.result?.hasOwnProperty('conditional')) {
																		approval_status = true;
																		approvalMessage = 'conditionally approved.'
																	} else if (res.result?.hasOwnProperty('mortgage')) {
																		approval_status = true;
																		approvalMessage = res.result?.mortgage;
																	} else {
																		if (res.result?.tierRange?.tier1Approved) {
																			approval_status = true;
																		}
																		if (
																			!res.result?.tierRange?.tier1Approved &&
																			res.result?.tierRange?.tier1FallBackApproved
																		) {
																			approval_status = false;
																			fallBackStatus = true;
																			fallBackAmount = res?.result?.tierRange?.tier1FallBackAmount;
																		}
																		if (
																			!res.result?.tierRange?.tier1Approved &&
																			res.result?.tierRange?.tier2Approved
																		) {
																			approval_status = false;
																		}
																		if (
																			!res.result?.tierRange?.tier1Approved &&
																			!res.result?.tierRange?.tier1FallBackApproved &&
																			!res.result?.tierRange?.tier2Approved
																		) {
																			approval_status = false;
																			declineMessage = 'LM Credit Criteria';
																		}
																	}

																	const usdFormatter = new Intl.NumberFormat('en-US', {
																		style: 'currency',
																		currency: 'USD',
																	});

																	let note = "";
																	if (approvalMessage) {
																		note += `File has been ${approvalMessage}. `;
																	}
																	note += `Rapid Result: ${res?.result.rapid_score_status}, Rapid Status: ${res?.result.rapid_status}, Rapid Stage: ${res?.result.rapid_stage}`;
																	if (res?.result.mortgageData) {
																		note += `, Mortgage Balance: ${usdFormatter.format(res?.result.mortgageData.mortgage_balance)}, Estimated Property Value: ${usdFormatter.format(res?.result.mortgageData.estimated_value)}, LTV: ${res?.result.mortgageData.ltv},`
																	}
																	this.saveRapidInternalResultNote(note);

																	const resultT = await this.updateTransunionApplication({
																		insertedTransunionID: insertedIDs.insertedTransunionID,
																		appID: { application_id: this.applicationId },
																		TU_Report: tuReport,
																	});
																	const resultR = await this.updateRapidClientApplication(
																		{
																			insertedRapidClientID: insertedIDs.insertedRapidClientID,
																			appID: { application_id: this.applicationId },
																		},
																		approval_status,
																		rapid_status,
																		data.otherCreditorObject,
																	);

																	this.application = {
																		...this.application,
																		Opportunity_Type: rapid_status
																	};

																	this.onSaveClick();

																	this.parentSub.add(this.applicationService.emailRapid({
																		...info,
																		applicationId: this.applicationId,
																		result: res?.result,
																		rapidStatus: rapid_status,
																	}).subscribe());
																	this.dialog.open(RapidResultModalComponent, {
																		width: '600px',
																		data: {
																			creditScore,
																			result: res.result,
																			approval_status,
																			rapid_status,
																			fallBackStatus,
																			fallBackAmount,
																			declineMessage,
																			approvalMessage,
																			receivedRapidData: info,
																		},
																	});
																}));

														} else {
															this._snackBar.open(
																'Creditors submit terminated',
																SnackbarConstants.ACTION_DISMISS,
																SnackbarConstants.DURATION_DISMISS_SHORT
															);
														}
													}));
												}
											}
										}));
								}
								else {
									this._bottomSheet.dismiss();
									this._snackBar.open(
										'No Hit was found. Please make sure the informations are correct.',
										SnackbarConstants.ACTION_DISMISS,
										SnackbarConstants.DURATION_DISMISS_SHORT
									);
								}
							}));
					}
				}));
			}
		}));
	}

	protected onSaveClick(): void {
		this.detailSaveDate = new Date();
	}

	protected onSaved(): void {
		this.savedDate = new Date();
	}

	// Open the selected Tab
	handleMenuChange(targetMenu: string) {
		this.selectedTab = targetMenu;
	}	

	/**
	 * Hotfix saha calculator
	 */
	isSahaCalculator: boolean = true;
	async refreshWaiveOffStatus(applicationId: string): Promise<void> {
		try {
		const res: boolean = await firstValueFrom(
			this.apiService.sideNavWaiveOffCalculator(applicationId)
		);
			this.isSahaCalculator = res;
		} catch (error) {
			console.error('Error fetching waive off eligibility:', error);
			this.isSahaCalculator = true;
		} 
  	}

	// Get the tab name
	protected get selectedTab(): string {
		return this._selectedTab;
	}

	// Add the seletectedTab name to the set 
	protected set selectedTab(tabId: string) {
		this._selectedTab = tabId;
		if (tabId) {
			// Split for chained children sub-menu, ex-> details-general -> details
			this.loadedTabs.add(tabId.split('-')[0]);
		}
	}

	// Tab on the set remain loaded via ngIf
	protected isTabLoaded(tab: string): boolean {
		return this.loadedTabs.has(tab);
	}
}

/**
 *
 * @param str url
 * @returns true if contains special characters
 */
function containsSpecialChars(str: string) {
	const specialChars = /[`1234567890!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
	return specialChars.test(str);
}
